import { RefObject, useEffect } from 'react';

export interface Point {
  x: number;
  y: number;
}

const SLOTS = 4;
const OFFSETS = [0, 0.18, 0.25, 0.43, 0.5, 0.68, 0.75, 0.93, 1];
const STEP_AT_OFFSET = [0, 0, 1, 1, 2, 2, 3, 3, 4];
const EASING = 'cubic-bezier(0.65, 0, 0.35, 1)';
const DURATION_MS = 24000;

export function buildCarouselSteps(centers: Point[]): Array<Array<[number, number]>> | null {
  if (centers.length !== SLOTS) return null;
  const cx = centers.reduce((sum, p) => sum + p.x, 0) / centers.length;
  const cy = centers.reduce((sum, p) => sum + p.y, 0) / centers.length;
  const slotOf = (p: Point): number => {
    const right = p.x >= cx;
    const bottom = p.y >= cy;
    if (!right && !bottom) return 0;
    if (right && !bottom) return 1;
    if (right && bottom) return 2;
    return 3;
  };
  const cornerCenter: Array<Point | undefined> = new Array(SLOTS);
  for (const p of centers) {
    const slot = slotOf(p);
    if (cornerCenter[slot]) return null;
    cornerCenter[slot] = p;
  }
  return centers.map((p) => {
    const start = slotOf(p);
    const seq: Array<[number, number]> = [];
    for (let k = 0; k <= SLOTS; k++) {
      const target = cornerCenter[(start + k) % SLOTS]!;
      seq.push([target.x - p.x, target.y - p.y]);
    }
    return seq;
  });
}

export function useLogoCarousel(ref: RefObject<HTMLElement>, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const svg = el.querySelector('svg');
    if (!svg) return;
    const shapes = Array.from(
      svg.querySelectorAll<SVGGraphicsElement>('path, circle, rect, ellipse, polygon')
    );
    if (shapes.length !== SLOTS) return;
    if (!svg.getBoundingClientRect().width) return;

    const centers: Point[] = shapes.map((shape) => {
      const r = shape.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    });

    const steps = buildCarouselSteps(centers);
    if (!steps) return;

    const animations = shapes
      .map((shape, i) => {
        const ctm = shape.getScreenCTM();
        if (!ctm || !ctm.a || !ctm.d) return null;
        const seq = steps[i];
        const keyframes = OFFSETS.map((offset, oi) => {
          const [dx, dy] = seq[STEP_AT_OFFSET[oi]];
          return { offset, translate: `${dx / ctm.a}px ${dy / ctm.d}px`, easing: EASING };
        });
        return shape.animate(keyframes, { duration: DURATION_MS, iterations: Infinity });
      })
      .filter((animation): animation is Animation => animation !== null);

    return () => animations.forEach((animation) => animation.cancel());
  }, [ref, enabled]);
}
